import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  Package,
  Shield,
  CheckCircle,
  FileKey,
  Activity,
  ExternalLink,
  Copy,
  Check,
  Terminal,
  Globe,
  Code,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  BookOpen,
  Lock,
  Github,
  Scale,
} from 'lucide-react'
import { api, PackageDetail, ToolDescriptor } from '../utils/api'

export default function ToolDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [pkg, setPkg] = useState<PackageDetail | null>(null)
  const [tools, setTools] = useState<ToolDescriptor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedCommand, setCopiedCommand] = useState(false)
  const [expandedTool, setExpandedTool] = useState<string | null>(null)

  useEffect(() => {
    async function loadPackage() {
      if (!slug) return

      setLoading(true)
      setError(null)
      console.log('[ToolDetailPage] Loading package:', slug)

      try {
        // First resolve slug to package_id
        const resolved = await api.resolvePackage(slug)
        const packageData = await api.getPackage(resolved.package_id)
        setPkg(packageData)

        // Load tools for latest version
        if (packageData.latest_version) {
          const toolsResponse = await api.getTools(resolved.package_id, packageData.latest_version)
          setTools(toolsResponse.items)
        }
      } catch (err) {
        console.error('[ToolDetailPage] Failed to load package:', err)
        setError('Package not found')
      } finally {
        setLoading(false)
      }
    }

    loadPackage()
  }, [slug])

  const copyInstallCommand = () => {
    const command = `xarlie install ${slug}`
    navigator.clipboard.writeText(command)
    setCopiedCommand(true)
    setTimeout(() => setCopiedCommand(false), 2000)
  }

  const getBadgeConfig = (badge: string) => {
    switch (badge) {
      case 'approved':
        return { icon: Shield, className: 'badge-approved', label: 'Approved' }
      case 'verified_publisher':
        return { icon: CheckCircle, className: 'badge-verified', label: 'Verified Publisher' }
      case 'signed_release':
        return { icon: FileKey, className: 'badge-signed', label: 'Signed Release' }
      case 'healthy_endpoint':
        return { icon: Activity, className: 'bg-emerald-100 text-emerald-800', label: 'Healthy Endpoint' }
      case 'validated_toolset':
        return { icon: Check, className: 'bg-cyan-100 text-cyan-800', label: 'Validated Skillset' }
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-16 h-16 bg-gray-200 rounded-xl" />
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
            <div className="card p-6">
              <div className="h-4 bg-gray-200 rounded w-full mb-4" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !pkg) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Package not found</h2>
          <p className="text-gray-500 mb-6">The skill you're looking for doesn't exist.</p>
          <Link to="/browse" className="btn btn-primary">
            Browse all skills
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li>
              <Link to="/browse" className="hover:text-gray-700">
                Browse
              </Link>
            </li>
            <li>/</li>
            <li className="text-gray-900">{pkg.name}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="flex items-start space-x-6 mb-8">
          {pkg.icon_url ? (
            <img
              src={pkg.icon_url}
              alt={pkg.name}
              className="w-20 h-20 rounded-xl object-cover"
            />
          ) : (
            <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-accent-100 rounded-xl flex items-center justify-center">
              <Package className="w-10 h-10 text-primary-600" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">{pkg.name}</h1>
            <p className="text-gray-500">{pkg.slug}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {pkg.open_source ? (
                <span className="badge bg-emerald-100 text-emerald-800">
                  <Code className="w-3 h-3 mr-1" />
                  Open Source
                </span>
              ) : (
                <span className="badge bg-gray-100 text-gray-600">
                  <Lock className="w-3 h-3 mr-1" />
                  Proprietary
                </span>
              )}
              {pkg.trust_tier === 'approved' ? (
                <span className="badge badge-approved">
                  <Shield className="w-3 h-3 mr-1" />
                  Approved
                </span>
              ) : (
                <span className="badge badge-unreviewed">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Unreviewed
                </span>
              )}
              {pkg.badges.filter(b => b !== 'approved').map((badge) => {
                const config = getBadgeConfig(badge)
                if (!config) return null
                return (
                  <span key={badge} className={`badge ${config.className}`}>
                    <config.icon className="w-3 h-3 mr-1" />
                    {config.label}
                  </span>
                )
              })}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700">
                {pkg.description || pkg.summary || 'No description provided.'}
              </p>
            </div>

            {/* Tools */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Skills ({tools.length})
              </h2>
              {tools.length === 0 ? (
                <p className="text-gray-500">No skills available</p>
              ) : (
                <div className="space-y-3">
                  {tools.map((tool) => (
                    <div
                      key={tool.tool_name}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() =>
                          setExpandedTool(
                            expandedTool === tool.tool_name ? null : tool.tool_name
                          )
                        }
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <Code className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{tool.title}</p>
                            <p className="text-sm text-gray-500 font-mono">
                              {tool.tool_name}
                            </p>
                          </div>
                        </div>
                        {expandedTool === tool.tool_name ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      {expandedTool === tool.tool_name && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                          {tool.description && (
                            <p className="text-sm text-gray-600 mb-4">
                              {tool.description}
                            </p>
                          )}
                          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-sm text-gray-300 font-mono">
                              {JSON.stringify(tool.input_schema, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Endpoints */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Endpoints</h2>
              {!pkg.endpoints || pkg.endpoints.length === 0 ? (
                <p className="text-gray-500">No endpoints configured</p>
              ) : (
                <div className="space-y-3">
                  {pkg.endpoints.map((endpoint, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Globe className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {endpoint.env}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {endpoint.base_url}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {endpoint.transport}
                        </span>
                        {endpoint.health_status === 'healthy' && (
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Install card */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Install</h2>
              <div className="bg-gray-900 rounded-lg p-3 flex items-center justify-between">
                <code className="text-sm text-gray-300 font-mono truncate">
                  xarlie install {slug}
                </code>
                <button
                  onClick={copyInstallCommand}
                  className="ml-2 p-1.5 text-gray-400 hover:text-white rounded"
                >
                  {copiedCommand ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Or use the CLI to subscribe and connect
              </p>
              <Link
                to="/docs/cli"
                className="mt-4 btn btn-secondary w-full flex items-center justify-center"
              >
                <Terminal className="w-4 h-4 mr-2" />
                Install CLI
              </Link>
            </div>

            {/* Info card */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Info</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Version</dt>
                  <dd className="text-gray-900 font-medium">
                    {pkg.latest_version || 'N/A'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Publisher</dt>
                  <dd>
                    <Link
                      to={`/creators/${pkg.publisher_id}`}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {pkg.publisher_name || pkg.publisher_id}
                    </Link>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">License</dt>
                  <dd className="text-gray-900 font-medium flex items-center">
                    <Scale className="w-3 h-3 mr-1 text-gray-400" />
                    {pkg.license || (pkg.open_source ? 'Open Source' : 'Proprietary')}
                  </dd>
                </div>
                {pkg.capabilities && pkg.capabilities.length > 0 && (
                  <div>
                    <dt className="text-gray-500 mb-1">Capabilities</dt>
                    <dd className="flex flex-wrap gap-1">
                      {pkg.capabilities.map((cap) => (
                        <span
                          key={cap}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {cap}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Links */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Links</h2>
              <div className="space-y-2">
                {pkg.repo_url && (
                  <a
                    href={pkg.repo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                  >
                    {pkg.repo_url.includes('github.com') ? (
                      <Github className="w-4 h-4 mr-2" />
                    ) : (
                      <Code className="w-4 h-4 mr-2" />
                    )}
                    Source Code
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
                {pkg.docs_url && (
                  <a
                    href={pkg.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Documentation
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
                {!pkg.repo_url && !pkg.docs_url && (
                  <p className="text-sm text-gray-500">No links available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
